import { useMemo } from 'react';
import type { TrajectoryPoint } from '@/types';

interface TrajectoryPlotProps {
  /** Trajectory points (10–100 yd typical). */
  points: TrajectoryPoint[];
  /** Build coefficient label shown bottom-right of the title block. */
  bc: number;
  /** Muzzle velocity for the header chip. */
  muzzleFps: number;
  /** Total arrow weight in grains for the header chip. */
  weightGr: number;
}

/**
 * Aerospace-grade ballistic trajectory plot.
 *
 * Sits above the existing TrajectoryTable in the Builder. The intent is to
 * convert a wall-of-numbers into an at-a-glance signal — the archer sees
 * the drop curve, the gate markers, and the Δ-vs-flat-line at 60 yd in
 * one visual.
 *
 * Visual vocabulary (per the v110 diagram-polish recommendation):
 *   - Cyan dashed reference  = "what a flat shot would do" (no drag, no drop)
 *   - Ember solid measured   = the actual computed trajectory
 *   - Gate markers at 20 / 40 / 60 / 80 / 100 yd with Δ annotations
 *   - Classification banner top + DOC/REV/SHEET right
 *   - Drag equation strip under the banner
 *   - Dark zinc canvas, fine grid, monospace labels — engineering blueprint
 *
 * The whole thing is one SVG that scales responsively; viewBox stays fixed
 * so coordinate math is stable across breakpoints.
 */
export function TrajectoryPlot({ points, bc, muzzleFps, weightGr }: TrajectoryPlotProps) {
  const VIEW_W = 1080;
  const VIEW_H = 280;

  // Plot rectangle inside the SVG (leaves room for axes + classification).
  const PLOT_X = 60;
  const PLOT_Y = 90;
  const PLOT_W = 960;
  const PLOT_H = 150;

  // Yard range — pick 0–100 by default, expanded if data goes further.
  const maxYards = useMemo(
    () => Math.max(100, ...points.map((p) => p.yards)),
    [points],
  );
  const maxDrop = useMemo(
    () =>
      Math.max(
        12, // minimum scale so the plot never feels empty for fast bows
        ...points.map((p) => Math.abs(p.dropInches)),
      ),
    [points],
  );

  // Coordinate transforms (yards → x, drop-inches → y).
  const xAt = (yd: number) => PLOT_X + (yd / maxYards) * PLOT_W;
  const yAt = (dropIn: number) => PLOT_Y + (Math.abs(dropIn) / maxDrop) * PLOT_H;

  // Build SVG path string from the trajectory points (smooth cubic
  // interpolation would be overkill; straight segments read fine and
  // accurately reflect what the integration produced).
  const trajectoryPath = useMemo(() => {
    if (points.length === 0) return '';
    return points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(p.yards).toFixed(1)} ${yAt(p.dropInches).toFixed(1)}`)
      .join(' ');
  }, [points, maxYards, maxDrop]);

  // Reference line: where a "flat" arrow (no drag, no drop) would land —
  // straight horizontal at zero drop. Cyan dashed. Useful as the visual
  // anchor against the ember measured curve.
  const referenceY = PLOT_Y;

  // Gate marks at 20/40/60/80/100 yd (only those within range) — these
  // are the practical shot distances for hunters and the natural markers
  // for the table comparison.
  const GATES = [20, 40, 60, 80, 100].filter((y) => y <= maxYards);

  // Highlight the 60-yd gate as the canonical "drag-tells-you-the-story" point.
  const sixtyPt = points.find((p) => p.yards === 60);

  // Drop and velocity readouts at each gate.
  const gatePts = GATES.map((yd) => {
    const p = points.find((pt) => pt.yards === yd);
    return p ? { yd, dropIn: p.dropInches, fps: p.velocityFps } : null;
  }).filter((g): g is { yd: number; dropIn: number; fps: number } => g !== null);

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="w-full"
      style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
      role="img"
      aria-label="Ballistic trajectory plot"
    >
      <defs>
        <pattern id="trajGrid" width="50" height="30" patternUnits="userSpaceOnUse">
          <path d="M 50 0 L 0 0 0 30" fill="none" stroke="#1f2937" stroke-width="0.4" />
        </pattern>
      </defs>

      {/* Canvas */}
      <rect width={VIEW_W} height={VIEW_H} fill="#09090b" />
      <rect x="20" y="20" width={VIEW_W - 40} height={VIEW_H - 40} fill="none" stroke="#27272a" strokeWidth="1" />

      {/* CLASSIFICATION BANNER */}
      <text x="36" y="44" fill="#22d3ee" fontSize="10" letterSpacing="3">
        BALLISTIC SOLVE · LEVEL LAUNCH · NO WIND · CONVERGED ✓
      </text>
      <text x={VIEW_W - 36} y="44" textAnchor="end" fill="#71717a" fontSize="10" letterSpacing="2">
        BC {bc.toFixed(2)} · μ {muzzleFps.toFixed(0)} fps · m {weightGr.toFixed(0)} gr
      </text>
      <line x1="20" y1="56" x2={VIEW_W - 20} y2="56" stroke="#27272a" strokeWidth="0.5" />

      {/* Drag equation strip */}
      <text x="36" y="76" fill="#71717a" fontSize="10" letterSpacing="2">
        v(t) = v₀ − ½ρCdAv²t · y(t) = −½g·t² · ρ_ISA = 1.225 kg/m³
      </text>

      {/* Plot frame */}
      <rect x={PLOT_X} y={PLOT_Y} width={PLOT_W} height={PLOT_H} fill="#0a0a0a" stroke="#27272a" strokeWidth="0.5" />
      <rect x={PLOT_X} y={PLOT_Y} width={PLOT_W} height={PLOT_H} fill="url(#trajGrid)" />

      {/* CH1 reference (cyan, dashed) — flat-line "no drop" */}
      <line
        x1={PLOT_X}
        y1={referenceY}
        x2={PLOT_X + PLOT_W}
        y2={referenceY}
        stroke="#22d3ee"
        strokeWidth="1.2"
        strokeDasharray="4 4"
        opacity="0.85"
      />
      <text x={PLOT_X + 8} y={referenceY - 6} fill="#22d3ee" fontSize="9" letterSpacing="1">
        CH1 · reference · level launch
      </text>

      {/* CH2 measured (ember, solid) — actual drop trajectory */}
      <path d={trajectoryPath} fill="none" stroke="#fb923c" strokeWidth="1.8" strokeLinejoin="round" />

      {/* Gate markers + Δ annotations */}
      {gatePts.map((g) => {
        const x = xAt(g.yd);
        const y = yAt(g.dropIn);
        return (
          <g key={g.yd}>
            {/* Vertical gate line */}
            <line
              x1={x}
              y1={PLOT_Y}
              x2={x}
              y2={PLOT_Y + PLOT_H}
              stroke="#3f3f46"
              strokeWidth="0.5"
              strokeDasharray="2 3"
            />
            {/* Point marker */}
            <circle cx={x} cy={y} r="3" fill="#fb923c" />
            {/* Drop callout — placed above the point unless near the top */}
            <text
              x={x}
              y={y - 8}
              textAnchor="middle"
              fill="#fb923c"
              fontSize="10"
              letterSpacing="0.5"
            >
              −{Math.abs(g.dropIn).toFixed(1)}"
            </text>
            {/* Yard label below the plot */}
            <text
              x={x}
              y={PLOT_Y + PLOT_H + 14}
              textAnchor="middle"
              fill="#71717a"
              fontSize="9"
            >
              {g.yd} yd
            </text>
            {/* Velocity-at-gate label below */}
            <text
              x={x}
              y={PLOT_Y + PLOT_H + 26}
              textAnchor="middle"
              fill="#52525b"
              fontSize="9"
            >
              {g.fps.toFixed(0)} fps
            </text>
          </g>
        );
      })}

      {/* Y-axis legend (top-right) */}
      <text x={PLOT_X - 8} y={PLOT_Y + 4} textAnchor="end" fill="#52525b" fontSize="9">
        0"
      </text>
      <text x={PLOT_X - 8} y={PLOT_Y + PLOT_H / 2 + 4} textAnchor="end" fill="#52525b" fontSize="9">
        −{(maxDrop / 2).toFixed(0)}"
      </text>
      <text x={PLOT_X - 8} y={PLOT_Y + PLOT_H + 4} textAnchor="end" fill="#52525b" fontSize="9">
        −{maxDrop.toFixed(0)}"
      </text>

      {/* Δ Annotation at 60 yd — the "drag-tells-you-the-story" callout */}
      {sixtyPt && (
        <g>
          <line
            x1={xAt(60) + 24}
            y1={referenceY}
            x2={xAt(60) + 24}
            y2={yAt(sixtyPt.dropInches)}
            stroke="#22d3ee"
            strokeWidth="0.7"
          />
          <line x1={xAt(60) + 20} y1={referenceY} x2={xAt(60) + 28} y2={referenceY} stroke="#22d3ee" strokeWidth="0.7" />
          <line
            x1={xAt(60) + 20}
            y1={yAt(sixtyPt.dropInches)}
            x2={xAt(60) + 28}
            y2={yAt(sixtyPt.dropInches)}
            stroke="#22d3ee"
            strokeWidth="0.7"
          />
          <text
            x={xAt(60) + 32}
            y={(referenceY + yAt(sixtyPt.dropInches)) / 2 + 3}
            fill="#22d3ee"
            fontSize="10"
            letterSpacing="0.5"
          >
            Δ {Math.abs(sixtyPt.dropInches).toFixed(1)}"
          </text>
        </g>
      )}
    </svg>
  );
}
